import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../core/data/redux/store";
import {
  fetchItDeclarationById,
  fetchItDeclarationConfigSets,
  fetchItDeclarationAttachments,
  fetchItDeclarationTypes,
  deleteItDeclaration,
  ItDeclarationItem,
} from "../../../core/data/redux/itDeclarationSlice";
import { all_routes } from "../../router/all_routes";
import { toast } from "react-toastify";

const routes = all_routes;

const ITDeclarationDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const user = useSelector((state: RootState) => (state as any).auth?.user);
  const empId = String(user?.details?.empId ?? user?.empId ?? "");

  const { selectedRequest, declarationTypes, attachments, loading } = useSelector(
    (state: RootState) => state.itDeclarationState
  );

  const [dynamicFields, setDynamicFields] = useState<any[]>([]);
  const [lineFieldsConfig, setLineFieldsConfig] = useState<any[]>([]);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const declTypeLabelMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (Array.isArray(declarationTypes)) {
      declarationTypes.forEach((item: any) => {
        const code = String(item?.ITDeclarationType || item?.value || "");
        const text = String(item?.ITDeclarationTypeText || item?.label || "");
        if (code && text) map[code] = text;
      });
    }
    return map;
  }, [declarationTypes]);

  const normalizeFieldRows = (rows: any[]): any[] =>
    (Array.isArray(rows) ? rows : [])
      .map((field: any) => ({
        ...field,
        key: field?.HeaderCustomerField || field?.LineCustomerField || "",
        label: String(field?.CustomerFieldLabel || field?.LineCustomerFieldLabel || "").replace(/\*\s*$/, "").trim(),
      }))
      .filter((f: any) => f.key)
      .sort((a: any, b: any) => Number(a?.FieldsOrder ?? 0) - Number(b?.FieldsOrder ?? 0));

  const displayFieldDate = (raw?: string | null): string => {
    if (!raw) return "";
    const match = String(raw).match(/\/Date\((-?\d+)\)\//);
    if (!match) return String(raw);
    const ms = Number(match[1]);
    if (ms >= 253402214400000) return "";
    const d = new Date(ms);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getUTCDate())}.${pad(d.getUTCMonth() + 1)}.${d.getUTCFullYear()}`;
  };

  const getFieldOptionLabel = (field: any, value: any): string => {
    if (value === null || value === undefined || value === "") return "";
    const raw = Array.isArray(field?.ToOptions) ? field.ToOptions : [];
    const match = raw.find(
      (o: any) => (o?.HeaderCustomerField || o?.CustomerFieldKey) === field?.key && String(o?.SelectedValue ?? "") === String(value)
    );
    return match ? String(match?.SelectedText ?? "") : String(value);
  };

  const getLineFieldOptionLabel = (field: any, value: any): string => {
    if (value === null || value === undefined || value === "") return "";
    const raw = Array.isArray(field?.ToLineOptions) ? field.ToLineOptions : [];
    const match = raw.find(
      (o: any) => (o?.LineCustomerField || o?.CustomerFieldKey) === field?.key && String(o?.SelectedValue ?? "") === String(value)
    );
    return match ? String(match?.SelectedText ?? "") : String(value);
  };

  const formatFieldValue = (field: any, raw: any, resolveOption: (f: any, v: any) => string): string => {
    if (field?.ViewOfFieldType === "C") {
      return raw === true || raw === "true" || raw === "X" ? "Yes" : "No";
    }
    if (field?.ViewOfFieldType === "" || /^cdt/i.test(String(field?.key ?? ""))) {
      return displayFieldDate(raw) || "-";
    }
    if (["E", "G", "D", "F"].includes(field?.ViewOfFieldType)) {
      return resolveOption(field, raw) || "-";
    }
    return raw === null || raw === undefined || raw === "" ? "-" : String(raw);
  };

  const fetchConfig = useCallback(
    async (raw: any) => {
      const txn = raw?.ToTransactionDatas ?? {};
      const declType = txn?.ReimbursementType || raw?.declarationType || "";
      if (!declType) return;
      setLoadingConfig(true);
      try {
        const payload = {
          empId: raw?.EmpID || raw?.EmpId || empId,
          declarationType: declType,
          type: txn?.TriggerRendering || "",
          propertyType: txn?.TriggerRendering1 || "",
          requestType: txn?.RequestType || "GA",
          operation: "EC",
        };
        const configRes = await dispatch(fetchItDeclarationConfigSets(payload)).unwrap();
        setDynamicFields(normalizeFieldRows(configRes?.data?.ToHeaders || configRes?.ToHeaders));
        setLineFieldsConfig(normalizeFieldRows(configRes?.data?.ToMultiLines || configRes?.ToMultiLines));
      } catch (err) {
        // config fetch fail safe
      } finally {
        setLoadingConfig(false);
      }
    },
    [dispatch, empId]
  );

  useEffect(() => {
    dispatch(fetchItDeclarationTypes({ forceSync: false }));
  }, [dispatch]);

  const handleDelete = async () => {
    setDeleteConfirmModalOpen(false);
    const raw = selectedRequest?.raw ?? {};
    const txn = raw?.ToTransactionDatas ?? {};
    const nowDate = `/Date(${Date.now()})/`;
    const payload = {
      Action: 'D',
      ReferenceNumber: selectedRequest?.referenceNumber || id,
      RequestedAmount: raw?.RequestedAmount ?? '0.00',
      ApprovedAmount: raw?.ApprovedAmount ?? '0.00',
      RequestedNumber: raw?.RequestedNumber ?? '0.00',
      ApprovedNumber: raw?.ApprovedNumber ?? '0.00',
      RequestedTaxExempt: raw?.RequestedTaxExempt ?? '0.00',
      ApprovedTaxExempt: raw?.ApprovedTaxExempt ?? '0.00',
      UnitKey: raw?.UnitKey ?? '',
      Currency: raw?.Currency ?? 'INR',
      ToDependants: {
        results: Array.isArray(raw?.ToDependants) ? raw.ToDependants : (raw?.ToDependants?.results ?? []),
      },
      ToRemarks: { results: remarkRows },
      ToAttachments: {
        results: attachments.length > 0
          ? attachments
          : (Array.isArray(raw?.ToAttachments) ? raw.ToAttachments : (raw?.ToAttachments?.results ?? [])),
      },
      ToDynamics: raw?.ToDynamics ?? {},
      ToMultiLines: { results: Array.isArray(raw?.ToMultiLines) ? raw.ToMultiLines : [] },
      ToTransactionDatas: {
        EmpID: raw?.EmpID,
        ReferenceNumber: selectedRequest?.referenceNumber || id,
        ReimbursementType: txn?.ReimbursementType,
        RequestType: txn?.RequestType,
        CreatedSource: txn?.CreatedSource ?? null,
        ReasonCode: txn?.ReasonCode ?? null,
        AppRejDelDate: nowDate,
        InfotypeUpdateDate: nowDate,
        PaymentDate: txn?.PaymentDate || nowDate,
        CompletedDate: txn?.CompletedDate || nowDate,
        InfotypeBeginDate: txn?.InfotypeBeginDate || nowDate,
        PhaseOftheRequest: '',
        RequestStatus: 'X',
        ApproverLevel: raw?.ApproverLevel,
        ApproverUserID: txn?.ApproverUserID,
        ApproverGroup: txn?.ApproverGroup,
        ReferenceNumber1: txn?.ReferenceNumber1 ?? null,
        TriggerRendering: txn?.TriggerRendering ?? null,
        TriggerRendering1: txn?.TriggerRendering1 ?? null,
        OffcycleRequested: txn?.OffcycleRequested ?? true,
        TaxExemRequired: txn?.TaxExemRequired ?? false,
        OffcycleProvided: txn?.OffcycleProvided ?? true,
        DeclAgreement: txn?.DeclAgreement ?? true,
        PartialClaim: txn?.PartialClaim ?? false,
        TripCompleted: txn?.TripCompleted ?? false,
        PartNumber: txn?.PartNumber ?? null,
      },
    };

    setIsDeleting(true);
    try {
      const res = await dispatch(deleteItDeclaration(payload)).unwrap();
      if (res?.success || res?.data?.success !== false) {
        toast.success("Request deleted successfully");
        navigate(routes.itDeclaration.list);
      } else {
        toast.error(res?.message || res?.data?.message || "Failed to delete request");
      }
    } catch (err: any) {
      toast.error(err?.message || "Failed to delete request");
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchItDeclarationById(id)).then((action: any) => {
        const raw = action.payload?.data ?? action.payload;
        if (raw) {
          fetchConfig(raw);
        }
      });
      dispatch(fetchItDeclarationAttachments(id));
    }
  }, [dispatch, id, fetchConfig]);

  const headerValueMap = useMemo(() => {
    const map: Record<string, any> = {};
    if (selectedRequest?.raw?.ToDynamics && typeof selectedRequest.raw.ToDynamics === "object") {
      Object.keys(selectedRequest.raw.ToDynamics).forEach((k) => {
        map[k.toUpperCase()] = selectedRequest.raw.ToDynamics[k];
      });
    }
    return map;
  }, [selectedRequest]);

  const visibleDynamicFields = useMemo(
    () => dynamicFields.filter((f: any) => f?.VisibleToEmployee),
    [dynamicFields]
  );

  const visibleLineColumns = useMemo(
    () => lineFieldsConfig.filter((f: any) => f?.VisibleToEmployee),
    [lineFieldsConfig]
  );

  const normalizedLineRows = useMemo(() => {
    const lineRows: any[] = Array.isArray(selectedRequest?.raw?.ToMultiLines)
      ? selectedRequest!.raw.ToMultiLines
      : [];
    return lineRows.map((row: any) => {
      const norm: Record<string, any> = {};
      if (row && typeof row === "object") {
        Object.keys(row).forEach((k) => {
          norm[k.toUpperCase()] = row[k];
        });
      }
      return norm;
    });
  }, [selectedRequest]);

  const remarkRows = useMemo(() => {
    const raw = selectedRequest?.raw;
    const list: any[] = Array.isArray(raw?.ToRemarks)
      ? raw.ToRemarks
      : Array.isArray(raw?.ToRemarks?.results)
      ? raw.ToRemarks.results
      : [];
    return list.filter((r: any) => r?.Comment || r?.EmployeeName);
  }, [selectedRequest]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "Completed":
        return <span className="badge bg-success-transparent text-success px-2 py-1 fs-12">Completed</span>;
      case "In-Process":
        return <span className="badge bg-warning-transparent text-warning px-2 py-1 fs-12">In-Process</span>;
      default:
        return <span className="badge bg-secondary-transparent text-secondary px-2 py-1 fs-12">To Be Processed</span>;
    }
  };

  if (loading || !selectedRequest) {
    return (
      <div className="page-wrapper">
        <div className="content text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading details...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header Breadcrumb */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div>
            <h3 className="page-title mb-1">IT Declaration Details</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.itDeclaration.list}>IT Declarations</Link>
                </li>
                <li className="breadcrumb-item active">Details ({selectedRequest.referenceNumber})</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2">
            {selectedRequest.statusCode !== "A" && (
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => setDeleteConfirmModalOpen(true)}
                disabled={isDeleting}
              >
                <i className={`ti ti-trash me-1 ${isDeleting ? "spin" : ""}`} />
                {isDeleting ? "Deleting..." : "Delete Request"}
              </button>
            )}
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => navigate(routes.itDeclaration.list)}
            >
              <i className="ti ti-arrow-left me-1" /> Back to List
            </button>
          </div>
        </div>

        {/* Status Banner */}
        <div className="card shadow-sm border mb-3 bg-light">
          <div className="card-body p-3 d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-3">
              <span className="fs-14 fw-semibold text-dark">Status:</span>
              {getStatusBadge(selectedRequest.status)}
              <span className="fs-13 text-muted">({selectedRequest.requestPhase})</span>
            </div>
            <div className="fs-13 text-muted">
              Reference: <strong className="text-dark">{selectedRequest.referenceNumber}</strong>
            </div>
          </div>
        </div>

        {/* Envelope Info */}
        <div className="row g-3 mb-3">
          <div className="col-md-4">
            <div className="card shadow-sm border h-100">
              <div className="card-header bg-white p-3">
                <h6 className="card-title mb-0 fw-semibold text-primary">
                  <i className="ti ti-briefcase me-1" /> Request Information
                </h6>
              </div>
              <div className="card-body p-3">
                <div className="row g-2 fs-13">
                  <div className="col-5 text-muted">Reference No:</div>
                  <div className="col-7 fw-medium text-dark">{selectedRequest.referenceNumber}</div>
                  <div className="col-5 text-muted">Declaration Type:</div>
                  <div className="col-7 fw-medium text-dark">
                    {declTypeLabelMap[selectedRequest.declarationType] || selectedRequest.declarationType}
                  </div>
                  <div className="col-5 text-muted">Request Type:</div>
                  <div className="col-7 fw-medium text-dark">{selectedRequest.requestType}</div>
                  <div className="col-5 text-muted">Employee Name / ID:</div>
                  <div className="col-7 fw-medium text-dark">{selectedRequest.employeeName}</div>
                  <div className="col-5 text-muted">Creation Date:</div>
                  <div className="col-7 fw-medium text-dark">{selectedRequest.creationDate || "-"}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="card shadow-sm border h-100">
              <div className="card-header bg-white p-3">
                <h6 className="card-title mb-0 fw-semibold text-primary">
                  <i className="ti ti-user-check me-1" /> Approver Details
                </h6>
              </div>
              <div className="card-body p-3">
                <div className="row g-2 fs-13">
                  <div className="col-5 text-muted">Approver:</div>
                  <div className="col-7 fw-medium text-dark">{selectedRequest.approver || "-"}</div>
                  <div className="col-5 text-muted">Approver ID:</div>
                  <div className="col-7 fw-medium text-dark">{selectedRequest.raw?.ApproverUserID || "-"}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="card shadow-sm border h-100">
              <div className="card-header bg-white p-3">
                <h6 className="card-title mb-0 fw-semibold text-primary">
                  <i className="ti ti-coin me-1" /> Financial Details
                </h6>
              </div>
              <div className="card-body p-3">
                <div className="row g-2 fs-13">
                  <div className="col-5 text-muted">Requested Amount:</div>
                  <div className="col-7 fw-medium text-dark">
                    {selectedRequest.currency} {selectedRequest.requestedValue.toFixed(2)}
                  </div>
                  <div className="col-5 text-muted">Approved Amount:</div>
                  <div className="col-7 fw-medium text-dark">
                    {selectedRequest.currency} {selectedRequest.approvedValue.toFixed(2)}
                  </div>
                  <div className="col-5 text-muted">Tax Exemption Approved:</div>
                  <div className="col-7 fw-medium text-dark">{selectedRequest.taxExemptApproved || "-"}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dynamic Declaration Header Fields */}
        {visibleDynamicFields.length > 0 && (
          <div className="card shadow-sm border mb-3">
            <div className="card-header bg-white p-3">
              <h6 className="card-title mb-0 fw-semibold text-primary">Declaration Details</h6>
            </div>
            <div className="card-body p-3">
              <div className="row g-3 fs-13">
                {visibleDynamicFields.map((field: any, idx: number) => {
                  const upperKey = String(field.key).toUpperCase();
                  const rawVal = headerValueMap[upperKey];
                  const formatted = formatFieldValue(field, rawVal, getFieldOptionLabel);
                  return (
                    <div className="col-md-4 col-sm-6" key={field.key || idx}>
                      <span className="text-muted d-block mb-1">{field.label}:</span>
                      <span className="fw-semibold text-dark">{formatted}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Dynamic Multiline Items Table */}
        {normalizedLineRows.length > 0 && visibleLineColumns.length > 0 && (
          <div className="card shadow-sm border mb-3">
            <div className="card-header bg-white p-3">
              <h6 className="card-title mb-0 fw-semibold text-primary">Deductions / Line Items Table</h6>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      {visibleLineColumns.map((col: any) => (
                        <th key={col.key}>{col.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {normalizedLineRows.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {visibleLineColumns.map((col: any) => {
                          const upperKey = String(col.key).toUpperCase();
                          const rawVal = row[upperKey];
                          const formatted = formatFieldValue(col, rawVal, getLineFieldOptionLabel);
                          return <td key={col.key}>{formatted}</td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Attachments List */}
        {attachments.length > 0 && (
          <div className="card shadow-sm border mb-3">
            <div className="card-header bg-white p-3">
              <h6 className="card-title mb-0 fw-semibold text-primary">Attachments</h6>
            </div>
            <div className="card-body p-3">
              <div className="row g-2">
                {attachments.map((doc: any, idx: number) => (
                  <div className="col-md-6" key={idx}>
                    <div className="p-2 border rounded d-flex align-items-center justify-content-between bg-light">
                      <div className="d-flex align-items-center">
                        <i className="ti ti-file-text fs-20 text-primary me-2" />
                        <div>
                          <span className="fw-medium text-dark d-block fs-13">{doc.FileName}</span>
                          <span className="text-muted fs-11">{doc.FileSize || "-"}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Previous Remarks Timeline */}
        {remarkRows.length > 0 && (
          <div className="card shadow-sm border mb-3">
            <div className="card-header bg-white p-3">
              <h6 className="card-title mb-0 fw-semibold text-primary">Approval Remarks History</h6>
            </div>
            <div className="card-body p-3">
              <ul className="timeline-list list-unstyled mb-0">
                {remarkRows.map((remark: any, idx: number) => (
                  <li key={idx} className="mb-3 pb-3 border-bottom">
                    <div className="d-flex align-items-center justify-content-between mb-1">
                      <span className="fw-semibold text-dark fs-13">{remark.EmployeeName || remark.CommentedByUserID}</span>
                    </div>
                    <p className="mb-0 text-muted fs-13">{remark.Comment || "No comment provided."}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirmModalOpen && (
          <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow-lg">
                <div className="modal-header bg-light">
                  <h5 className="modal-title fw-bold text-dark">Confirm Delete Request</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setDeleteConfirmModalOpen(false)}
                  />
                </div>
                <div className="modal-body p-4 text-center">
                  <i className="ti ti-alert-triangle fs-48 text-danger mb-3 d-block" />
                  <p className="fs-14 text-dark mb-0 fw-medium">
                    Are you sure you want to delete IT Declaration request{" "}
                    <strong>{selectedRequest.referenceNumber}</strong>?
                  </p>
                </div>
                <div className="modal-footer bg-light justify-content-center">
                  <button
                    type="button"
                    className="btn btn-outline-secondary px-4"
                    onClick={() => setDeleteConfirmModalOpen(false)}
                    disabled={isDeleting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger px-4 fw-semibold"
                    onClick={handleDelete}
                    disabled={isDeleting}
                  >
                    {isDeleting ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ITDeclarationDetailsPage;
